import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { BsPeople } from 'react-icons/bs';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { Actions } from '../../../components/Actions';
import { AdminTitle } from '../../../components/AdminTitle';
import { Loading } from '../../../components/Loading';
import { StyledInput } from '../../../components/StyledInput';

import { emailValidation } from '../../../utils/EmailValidation';
import { TYPE_DOCUMENT } from '../../../utils/enums';
import { formatDocument } from '../../../utils/formatDocument';

import { useQueryClient } from 'react-query';
import { createUser, getUserById, updateUser } from '../../../hooks/useUsers';
import theme from '../../../styles/themes/default';
import formatMobilePhoneNumber from '../../../utils/formatMobilePhoneNumber';
import { rolesOptions } from '../../../utils/rolesOptions';
import { statusOptions } from '../../../utils/statusOptions';
import { validateCnpj } from '../../../utils/validateCpf';
import { validateMobilePhone } from '../../../utils/validateMobilePhone';
import { isValidPassword } from '../../../utils/verifyPassword';
import InputSelect from './components/InputSelect';
import { Card, Container, FormContainer, FormWrapper } from './style';

const maxBirthDate = new Date();
maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 18);

const UsersForm: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);

  const [name, setName] = useState<string>('');
  const [document, setDocument] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [status, setStatus] = useState<'ATIVO' | 'BLOQUEADO' | string>('');
  const [role, setRole] = useState<string>('');

  const requiredField = (value: any) => {
    return value ? '' : 'Campo obrigatório';
  };

  const emailError = useMemo(() => {
    const result = emailValidation({
      email,
      emptyEmail: 'Campo obrigatório',
      incorrectEmail: 'Email inválido',
    });
    return result.message;
  }, [email]);

  const cnpjError = useMemo(() => {
    const result = validateCnpj({
      cnpj: document,
      emptyCnpj: 'Campo obrigatório',
      incorrectCnpj: 'CNPJ inválido',
    });
    return result.message;
  }, [document]);

  const phoneError = useMemo(() => {
    const result = validateMobilePhone({
      cellphone: phone,
      emptyCellphone: 'Campo obrigatório',
      incorrectCellphone: 'Telefone inválido',
    });
    return result.message;
  }, [phone]);

  const documentError = cnpjError;
  const handleFormValidate = useCallback(() => {
    const validations = [
      requiredField(name),
      requiredField(document),
      documentError,
      requiredField(email),
      phoneError,
      requiredField(phone),
      emailError,
      requiredField(status),
      requiredField(role),
    ];
    return !validations.some((v) => v);
  }, [
    name,
    document,
    documentError,
    email,
    phoneError,
    phone,
    emailError,
    status,
    role,
  ]);

  const handleLoadUser = async () => {
    try {
      setLoading(true);
      const user = await getUserById(Number(id));
      setName(user.name);
      setDocument(
        formatDocument(
          user.document,
          TYPE_DOCUMENT.CNPJ,
        ),
      );
      setEmail(user.email);
      setPhone(formatMobilePhoneNumber(user.phone));
      setPassword(user.password);
      setStatus(user.status);
      setRole(user.role);
    } catch (error: any) {
      console.log(error);
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html: error?.response?.data?.message ?? `Usuário não encontrado.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setValidating(true);
    if (!handleFormValidate()) {
      Swal.fire({
        icon: 'error',
        title: 'Campos inválidos!',
        html: `Alguns campos ainda estão inválidos. Corrija-os e tente novamente.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }

    if (!id && !password) {
      Swal.fire({
        icon: 'error',
        title: 'Senha inválida!',
        html: `A senha é obrigatória para a criação de um usuário.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }

    if (!id && !isValidPassword(password)) {
      Swal.fire({
        icon: 'error',
        title: 'Senha inválida!',
        html: `Senha inválida. A senha deve conter pelo menos uma letra maiúscula, uma letra minúscula, um dígito, um caractere especial e ter no mínimo 8 caracteres de comprimento.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
      return;
    }

    try {
      setLoading(true);
      const cleanedDocument = document.replace(/\D/g, '');
      const rawPhone = phone?.replace(/\D/g, '');

      const payload = {
        name,
        document: cleanedDocument,
        email,
        phone: rawPhone,
        role,
        status,
      };

      if (id) {
        await updateUser({ id: Number(id), ...payload });
      }
      if (!id) {
        await createUser({ ...payload, password });
      }

      Swal.fire({
        icon: 'success',
        title: 'Sucesso!',
        html: `Usuário ${id ? 'editado' : 'cadastrado'} com sucesso.`,
        confirmButtonColor: theme.colors.orange.primary,
      });

      queryClient.invalidateQueries('user_frotas');
      navigate('/users');
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        html:
          error.response.data.message ??
          `Não foi possível salvar este registro.`,
        confirmButtonColor: theme.colors.orange.primary,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      handleLoadUser();
    }
  }, [id]);

  return (
    <Container>
      {loading && <Loading />}
      <AdminTitle
        title="Novo cadastro"
        icon={<BsPeople size={24} color="#4D4C4D" />}
      />
      <FormContainer onSubmit={handleSubmit}>
        <Card>
          <h2>Informações Gerais</h2>
          <FormWrapper>
            <label htmlFor="">
              <StyledInput
                placeholder="Nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={validating && !!requiredField(name)}
                errorMessage={requiredField(name)}
                maxLength={255}
              />
            </label>
            <label style={{ maxWidth: '32.7%' }} htmlFor="">
              <StyledInput
                placeholder="CNPJ"
                value={document}
                onChange={(e) => {
                  const formatted = formatDocument(e.target.value, TYPE_DOCUMENT.CNPJ);
                  setDocument(formatted);
                }}
                maxLength={18}
                error={validating && !!documentError}
                errorMessage={documentError}
              />
            </label>
            {!id && (
              <label style={{ maxWidth: '32.7%' }} htmlFor="">
                <StyledInput
                  placeholder="Senha"
                  value={password}
                  type="password"
                  onChange={(e) => setPassword(e.target.value)}
                  error={validating}
                />
              </label>
            )}
          </FormWrapper>
          <FormWrapper error={validating}>
            <label htmlFor="">
              <StyledInput
                placeholder="Telefone"
                value={phone}
                maxLength={15}
                onChange={(e) => {
                  const phoneMapped = formatMobilePhoneNumber(e.target.value);

                  setPhone(phoneMapped);
                }}
                error={validating && !!phoneError}
                errorMessage={phoneError}
              />
            </label>
            <label htmlFor="">
              <StyledInput
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={validating && !!emailError}
                errorMessage={emailError}
              />
            </label>
            <label htmlFor="">
              <InputSelect
                error={validating && !!requiredField(role)}
                errorMessage={requiredField(role)}
                placeHolder={'Selecione uma função...'}
                options={rolesOptions}
                onChange={(event) => setRole(event.target.value)}
                value={role}
              />
            </label>
            <label htmlFor="">
              <InputSelect
                error={validating && !!requiredField(status)}
                errorMessage={requiredField(status)}
                placeHolder={'Selecione um status...'}
                options={statusOptions}
                onChange={(event) => setStatus(event.target.value)}
                value={status}
              />
            </label>
          </FormWrapper>
        </Card>

        <Actions
          onCancel={() => {
            navigate('/users');
          }}
        />
      </FormContainer>
    </Container>
  );
};

export default UsersForm;
